'use strict';

/**
 * NACRE Wave 2.1.5 — hybrid signatures (Ed25519 + ML-DSA-65)
 *
 * Hybrid post-quantum digital signatures. Used to authenticate messages,
 * file uploads, capability grants, and any other artifact that needs
 * sender attestation.
 *
 * The hybrid signature is the CONCATENATION of an Ed25519 signature and
 * an ML-DSA-65 signature over the SAME message. To verify, the recipient
 * checks BOTH signatures independently and accepts only if BOTH are valid.
 *
 * An attacker would need to forge BOTH a classical Ed25519 sig AND a
 * lattice-based ML-DSA sig, simultaneously, on the same message. As of
 * 2026, neither is feasible — and even when quantum computers eventually
 * break Ed25519, ML-DSA holds.
 *
 * Signature layout:
 *   bytes [0..63]    = Ed25519 signature (64 bytes)
 *   bytes [64..3372] = ML-DSA-65 signature (3309 bytes)
 *   total = 3373 bytes
 *
 * Note that ML-DSA signatures are NOT deterministic — they use fresh
 * randomness during signing. So two signatures of the same message under
 * the same key will share the first 64 bytes (Ed25519 IS deterministic
 * per RFC 8032) but differ in the next 3309 bytes (ML-DSA).
 *
 * Sizes:
 *   Ed25519 pub:  32 bytes
 *   Ed25519 priv: 32 bytes
 *   Ed25519 sig:  64 bytes
 *   ML-DSA pub:   1952 bytes
 *   ML-DSA priv:  4032 bytes
 *   ML-DSA sig:   3309 bytes
 *
 * Total signing keypair: 32 + 4032 = 4064 private bytes, 32 + 1952 = 1984 public bytes.
 * Total signature: 64 + 3309 = 3373 bytes.
 */

const { ed25519 } = require('@noble/curves/ed25519.js');
const { ml_dsa65 } = require('@noble/post-quantum/ml-dsa.js');

const TEXT_ENCODER = new TextEncoder();

const SIG_SIZES = Object.freeze({
  ed25519PublicKey: 32,
  ed25519PrivateKey: 32,
  ed25519Signature: 64,
  mlDsaPublicKey: 1952,
  mlDsaPrivateKey: 4032,
  mlDsaSignature: 3309,
  totalSignature: 64 + 3309,
});

function toBytes(msg) {
  if (msg instanceof Uint8Array) return msg;
  if (typeof msg === 'string') return TEXT_ENCODER.encode(msg);
  throw new TypeError('Invalid message: must be string or Uint8Array');
}

function validateHybridPrivateKey(priv) {
  if (!priv || typeof priv !== 'object') {
    throw new TypeError('Invalid privateKey: must be {ed25519, mlDsa} object');
  }
  if (!(priv.ed25519 instanceof Uint8Array) || priv.ed25519.length !== SIG_SIZES.ed25519PrivateKey) {
    throw new Error('Invalid privateKey.ed25519: must be 32 bytes');
  }
  if (!(priv.mlDsa instanceof Uint8Array) || priv.mlDsa.length !== SIG_SIZES.mlDsaPrivateKey) {
    throw new Error('Invalid privateKey.mlDsa: must be 4032 bytes');
  }
}

/**
 * Generate a fresh hybrid signing keypair.
 *
 * @returns {{publicKey: {ed25519: Uint8Array, mlDsa: Uint8Array},
 *           privateKey: {ed25519: Uint8Array, mlDsa: Uint8Array}}}
 */
function generateKeyPair() {
  const edPriv = ed25519.utils.randomSecretKey();
  const edPub = ed25519.getPublicKey(edPriv);
  const dsaKp = ml_dsa65.keygen();
  return {
    publicKey: {
      ed25519: edPub,
      mlDsa: dsaKp.publicKey,
    },
    privateKey: {
      ed25519: edPriv,
      mlDsa: dsaKp.secretKey,
    },
  };
}

/**
 * Sign a message with the hybrid private key. Returns a single concatenated
 * signature: Ed25519 sig (64 bytes) || ML-DSA sig (3309 bytes).
 *
 * @param {{ed25519: Uint8Array, mlDsa: Uint8Array}} privateKey
 * @param {string|Uint8Array} message
 * @returns {Uint8Array} 3373-byte signature
 */
function sign(privateKey, message) {
  validateHybridPrivateKey(privateKey);
  const msgBytes = toBytes(message);

  const edSig = ed25519.sign(msgBytes, privateKey.ed25519);
  const dsaSig = ml_dsa65.sign(msgBytes, privateKey.mlDsa);

  const combined = new Uint8Array(SIG_SIZES.totalSignature);
  combined.set(edSig, 0);
  combined.set(dsaSig, SIG_SIZES.ed25519Signature);
  return combined;
}

/**
 * Verify a hybrid signature. Returns true ONLY if BOTH halves verify.
 *
 * Returns `false` (never throws) for any malformed input — easier to use
 * in caller paths that need a yes/no answer.
 *
 * @param {{ed25519: Uint8Array, mlDsa: Uint8Array}} publicKey
 * @param {string|Uint8Array} message
 * @param {Uint8Array} signature — 3373-byte concatenated signature
 * @returns {boolean}
 */
function verify(publicKey, message, signature) {
  if (!publicKey || typeof publicKey !== 'object') return false;
  if (!(publicKey.ed25519 instanceof Uint8Array) || publicKey.ed25519.length !== SIG_SIZES.ed25519PublicKey) return false;
  if (!(publicKey.mlDsa instanceof Uint8Array) || publicKey.mlDsa.length !== SIG_SIZES.mlDsaPublicKey) return false;
  if (!(signature instanceof Uint8Array) || signature.length !== SIG_SIZES.totalSignature) return false;

  let msgBytes;
  try { msgBytes = toBytes(message); } catch (_e) { return false; }

  const edSig = signature.slice(0, SIG_SIZES.ed25519Signature);
  const dsaSig = signature.slice(SIG_SIZES.ed25519Signature);

  let edOk = false;
  let dsaOk = false;
  try { edOk = ed25519.verify(edSig, msgBytes, publicKey.ed25519); } catch (_e) { return false; }
  try { dsaOk = ml_dsa65.verify(dsaSig, msgBytes, publicKey.mlDsa); } catch (_e) { return false; }

  // CRITICAL: must be AND, not OR. Both halves must be valid for the
  // hybrid construction to provide its security guarantee.
  return edOk && dsaOk;
}

module.exports = {
  generateKeyPair,
  sign,
  verify,
  SIG_SIZES,
};
